{#if showSearch}
    <SearchInput
        label={searchLabel}
        placeholder={searchPlaceholder}
        bind:value={searchValue}
        on:keydown={handleSearch}
        on:clear={handleSearch}
    />
{/if}

<div class="screen-reader" aria-live="polite">
    {#if loading}
        {lang.main('Loading')}
    {/if}
</div>

{#if pageItems?.length > 0}
    <List>
        <thead slot="header">
            <ListElement header>
                <CheckboxCell
                    disabled={searchValue.length > 0}
                    header

                    bind:checked={isAllSelected}
                    text={lang.main("Select_all_visible", pageItems.length)}
                    on:change={selectAll}
                />

                <slot name="headerTemplate"></slot>
            </ListElement>
        </thead>
        <tbody slot="body">
            {#each pageItems as item}
                <ListElement
                    bind:checked={item.checked}
                    checkOnClick
                    on:click={selectItemChanged}
                >

                    <CheckboxCell
                        bind:checked={item.checked}
                        text={lang.main('Select_x', getNameFn(item.value))}
                        on:change={selectItemChanged}
                    />

                    <slot name="rowTemplate" item={item.value} />
                </ListElement>
            {/each}
        </tbody>
    </List>

    <div class="itsl-participants__pagination">
        <Pagination
            i18n={paginationI18n}
            showCompactView
            showNumberOfItems
            showPageSize
            singlePageApp
            {totalCount}
            {pageSizesList}
            bind:pageSize={pageSize}
            bind:currentPage={currentPage}
            on:previousPage={reloadPage}
            on:nextPage={reloadPage}
            on:pageSizeChange={reloadPage}
        />
    </div>
{:else if !loading}
    <EmptyState
        iconName={emptyStateIconValue}
        title={emptyStateTitle}
        description={emptyStateDescription}
        showLink={emptyStateLinkUrl}
        linkType="standalone"
        linkText={emptyStateLinkText}
        href={emptyStateLinkUrl}
        target="_blank"
    />
{:else if loading}
    <div class="itsl-participants__loading">
        <LoadingSpinner
            visible
            label={lang.main("Loading")}
        />
    </div>
{/if}

<script lang="ts">
    import { onMount, createEventDispatcher } from "svelte";

    import CheckboxCell from "@itslearning/prometheus/assets/lists/CheckboxCell/v1/CheckboxCell.svelte";
    import EmptyState from "@itslearning/prometheus/assets/nodes/EmptyState/v1/EmptyState.svelte";
    import List from "@itslearning/prometheus/assets/lists/List/v1/List.svelte";
    import ListElement from "@itslearning/prometheus/assets/lists/ListElement/v1/ListElement.svelte";
    import LoadingSpinner from "@itslearning/prometheus/assets/nodes/LoadingSpinner/v1/LoadingSpinner.svelte";
    import Pagination from "@itslearning/prometheus/assets/Navigation/Pagination/v1/Pagination.svelte";
    import SearchInput from "@itslearning/prometheus/assets/inputs/SearchInput/v1/SearchInput.svelte";

    import { debounce } from "@itslearning/atlas/dom/Ratecontrol";

    import { participantsPaginationPageSizes } from '../../constants';

    import { lang } from "@its-ui/mono/lang/provider";
    import { getPaginationI18n } from "@its-ui/mono/lang/pack/pagination";

    import type { GetItemsFn } from '../courseParticipantsDataProvider';

    const dispatch = createEventDispatcher();

    const paginationI18n = getPaginationI18n();

    export let getItemsFn: GetItemsFn;

    export let searchLabel;
    export let searchPlaceholder;
    export let searchValue = '';

    export let pageSizesList = participantsPaginationPageSizes;
    export let selectedIds: number[];
    export let selectedTitle: string = undefined;
    export let getNameFn: (obj: any) => string;
    export let searchEmptyTitle: string;
    export let searchEmptyDescription: string;
    export let emptyTitle: string;
    export let emptyDescription: string;
    export let emptyStateLinkText: string;
    export let emptyStateLinkUrl: string;
    export let emptyStateIcon: string;
    export let selectedTitles: Map<number, string> = undefined;

    let isAllSelected = false;

    $: emptyStateTitle = searchValue
        ? searchEmptyTitle
        : emptyTitle;
    $: emptyStateDescription = searchValue
        ? searchEmptyDescription
        : emptyDescription;
    $: emptyStateIconValue = searchValue
        ? "search-no-result"
        : emptyStateIcon;

    let loading = false;

    let currentPage = 1;
    let pageSize = participantsPaginationPageSizes[0];
    let totalCount;

    let pageItems: {id: number; value: any; checked?: boolean}[] = [];

    $: showSearch = (pageItems && pageItems.length > 0) || searchValue;

    onMount(async () => {
        pageItems = [];
        currentPage = 1;
        await loadPage();
        updateAllSelected();
    });

    async function loadPage() {
        try {
            loading = true;

            const data = await getItemsFn(currentPage - 1, pageSize, searchValue);

            totalCount = data.totalCount;
            for (let item of data.items) {
                item.checked = selectedIds.includes(item.id);
            }

            pageItems = data.items;
        } catch (err) {
            dispatch("error");
        } finally {
            loading = false;
        }
    }

    async function reloadPage() {
        await loadPage();
        updateAllSelected();
    }

    const handleSearch = debounce(async () => {
        currentPage = 1;
        await reloadPage();
    }, 200, null);

    function selectAll() {
        pageItems.forEach(item => {
            item.checked = isAllSelected;
        });

        selectItemChanged();
        updateTitles();

        pageItems = pageItems;
        selectedIds = selectedIds;
    }

    function selectItemChanged() {
        const set = new Set(selectedIds);
        for (const e of pageItems) {
            e.checked ? set.add(e.id) : set.delete(e.id);
        }
        selectedIds = Array.from(set);

        updateTitles();
        updateAllSelected();
    };

    function updateTitles() {
        if (selectedTitles && getNameFn) {
            selectedIds.forEach(id => {
                const item = pageItems.find(e => e.id === id);
                if (item) {
                    selectedTitles.set(id, getNameFn(item.value));
                }
            });
            if (selectedIds.length === 1) {
                selectedTitle = selectedTitles.get(selectedIds[0]);
            }
        }
    }

    function updateAllSelected() {
        isAllSelected = !pageItems.some(item => !item.checked);
    }
</script>
